/**
 * RoomRateEditForm.jsx
 * Popup para editar una Room Rate completa
 */

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";

const WEEKDAY_OPTIONS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const emptyWeekdayValues = () =>
  WEEKDAY_OPTIONS.reduce((acc, day) => {
    acc[day.key] = "";
    return acc;
  }, {});

const toWeekdayFormValues = (source) => {
  const values = emptyWeekdayValues();
  if (!source || typeof source !== "object") return values;

  for (const day of WEEKDAY_OPTIONS) {
    const raw = source[day.key];
    values[day.key] = raw === null || typeof raw === "undefined" ? "" : String(raw);
  }
  return values;
};

const normalizeWeekdayPayload = (source) => {
  const normalized = {};
  let hasAny = false;

  for (const day of WEEKDAY_OPTIONS) {
    const raw = source?.[day.key];
    if (raw === "" || raw === null || typeof raw === "undefined") {
      normalized[day.key] = null;
      continue;
    }

    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1) {
      return { value: null, error: `${day.label}: value must be an integer >= 1` };
    }

    normalized[day.key] = n;
    hasAny = true;
  }

  return { value: hasAny ? normalized : null, error: null };
};

const validateStayRanges = ({ minDefault, maxDefault, minByWeekday, maxByWeekday }) => {
  if (minDefault !== null && maxDefault !== null && minDefault > maxDefault) {
    return "Default Min Stay cannot be greater than Default Max Stay.";
  }

  for (const day of WEEKDAY_OPTIONS) {
    const minV = minByWeekday?.[day.key] ?? null;
    const maxV = maxByWeekday?.[day.key] ?? null;
    if (minV !== null && maxV !== null && minV > maxV) {
      return `${day.label}: Default Min Stay cannot be greater than Default Max Stay.`;
    }
  }

  return null;
};

export const getRoomRateEditPrecisionError = (formData, currencyCatalog = []) => {
  const currency = String(formData?.currency || "").trim().toUpperCase();
  const minorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  const invalidPrice = [formData?.min_price_amount, formData?.max_price_amount].some(
    (value) => value !== "" && value !== null && value !== undefined &&
      !hasValidCurrencyPrecision(value, currency, currencyCatalog)
  );
  if (invalidPrice) {
    return `${currency} price limits accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
  }
  if (
    formData?.penalty_type === "fixed" &&
    formData?.penalty_amount !== "" &&
    formData?.penalty_amount !== null &&
    formData?.penalty_amount !== undefined &&
    !hasValidCurrencyPrecision(formData.penalty_amount, currency, currencyCatalog)
  ) {
    return `${currency} fixed penalties accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
  }
  return null;
};

const formShellStyle = {
  width: "100%",
  maxWidth: "960px",
  margin: "0 auto",
  borderRadius: "16px",
  padding: "22px",
  background: "var(--erp-modal-bg)",
  border: "1px solid var(--erp-modal-border)",
  borderTop: "3px solid #3db6e9",
  boxShadow: "var(--erp-modal-shadow)",
  color: "var(--erp-modal-text)",
};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "12px",
  padding: "16px",
  background: "var(--erp-modal-section)",
  border: "1px solid var(--erp-modal-border)",
};

const sectionHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.1rem",
  fontWeight: 800,
  color: "var(--erp-modal-title)",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.83rem",
  color: "var(--erp-modal-muted)",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const labelStyle = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "var(--erp-modal-text)",
};

const hintTextStyle = {
  fontSize: "0.78rem",
  color: "var(--erp-modal-muted)",
  marginTop: "4px",
};

const chipWrapperStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  minHeight: "64px",
  padding: "10px",
  borderRadius: "12px",
  border: "1px solid var(--erp-modal-border-strong)",
  background: "var(--erp-modal-input)",
};

const chipTagStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  borderRadius: "999px",
  padding: "6px 10px",
  border: "1px solid rgba(61, 182, 233, 0.68)",
  color: "var(--erp-modal-title)",
  background: "var(--erp-modal-accent-soft)",
  fontSize: "0.85rem",
  fontWeight: 600,
};

const chipCloseStyle = {
  border: "none",
  background: "transparent",
  color: "var(--erp-modal-danger)",
  fontSize: "1rem",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  lineHeight: 1,
};

const weekdayGridStyle = {
  marginTop: "12px",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "14px",
};

const weekdayCardStyle = {
  borderRadius: "12px",
  border: "1px solid var(--erp-modal-border)",
  padding: "12px",
  background: "var(--erp-modal-section-strong)",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const weekdayInputStyle = {
  borderRadius: "10px",
  padding: "6px 10px",
  background: "var(--erp-modal-input)",
  border: "1px solid var(--erp-modal-border-strong)",
  color: "var(--erp-modal-text)",
};

const toggleButtonBase = {
  borderRadius: "999px",
  border: "1px solid",
  padding: "8px 18px",
  fontSize: "0.85rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "transform 120ms ease, background 120ms ease, border-color 120ms ease",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input)",
  border: "1px solid var(--erp-modal-border-strong)",
  color: "var(--erp-modal-text)",
  fontSize: "0.95rem",
};

const selectFieldStyle = {
  ...inputFieldStyle,
  appearance: "none",
};

const textareaFieldStyle = {
  width: "100%",
  minHeight: "110px",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "var(--erp-modal-input)",
  border: "1px solid var(--erp-modal-border-strong)",
  color: "var(--erp-modal-text)",
  fontSize: "0.95rem",
  resize: "vertical",
};

const statusBadgeStyle = {
  padding: "6px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(99, 193, 199, 0.78)",
  color: "var(--erp-modal-positive)",
  background: "var(--erp-modal-positive-soft)",
  fontWeight: 700,
};

const muteBadgeStyle = {
  padding: "6px 14px",
  borderRadius: "999px",
  border: "1px solid rgba(155, 52, 69, 0.48)",
  color: "var(--erp-modal-danger)",
  background: "var(--erp-modal-danger-soft)",
  fontWeight: 700,
};

const fieldActionsRowStyle = {
  marginTop: "24px",
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const primaryButtonStyle = {
  borderRadius: "12px",
  border: "1px solid #223a7a",
  background: "#223a7a",
  color: "#ffffff",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
};

const secondaryButtonStyle = {
  borderRadius: "12px",
  border: "1px solid var(--erp-modal-danger-border)",
  background: "var(--erp-modal-danger-soft)",
  color: "var(--erp-modal-danger)",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
};

export default function RoomRateEditForm({
  rate,
  businessUnitId,
  onSuccess,
  onCancel
}) {
  const { themeId } = usePmsTheme();
  const { currencyCatalog = [] } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const [formData, setFormData] = useState(null);
  const [clients, setClients] = useState([]);
  const [roomCategories, setRoomCategories] = useState([]);
  const [capacityLimits, setCapacityLimits] = useState({
    adults: null,
    children: null,
    max_occupancy: null
  });

  // ===============================
// LOAD INITIAL DATA  (CORREGIDO)
// ===============================
useEffect(() => {
  if (!rate) return;

  // Inicialización básica

    setFormData({
        rate_code: rate.rate_code || "",
        name: rate.name || "",
        client_id: rate.client_id || "",
        room_category_id: "",
        room_category_ids: rate.group_key
        ? [] 
        : [rate.room_category_id],
        group_key: rate.group_key || "",
        currency: rate.currency || "EUR",
        valid_from: rate.valid_from?.split("T")[0] || "",
        valid_to: rate.valid_to?.split("T")[0] || "",
        status: rate.status || "active",
        adults: rate.adults ?? "",
        adults_match_type: rate.adults_match_type || "exact",

        children: rate.children ?? "",
        children_match_type: rate.children_match_type || "upto",

        max_occupancy: rate.max_occupancy ?? "",
        max_occupancy_match_type: rate.max_occupancy_match_type || "upto",

        is_refundable: rate.is_refundable ?? true,

        // ✅ NEW
        show_in_booking_engine: rate.show_in_booking_engine ?? true,

        cancellation_policy: rate.cancellation_policy || "",
        penalty_type: rate.penalty_type || "",
        penalty_amount: rate.penalty_amount ?? "",
        penalty_description: rate.penalty_description || "",

        // ✅ Rate price limits + default stays (RoomRates columns)
        min_price_amount: rate.min_price_amount ?? "",
        max_price_amount: rate.max_price_amount ?? "",
        default_min_stay: rate.default_min_stay ?? "",
        default_max_stay: rate.default_max_stay ?? "",
        use_stay_defaults_by_weekday:
          !!(rate.default_min_stay_by_weekday || rate.default_max_stay_by_weekday),
        default_min_stay_by_weekday: toWeekdayFormValues(rate.default_min_stay_by_weekday),
        default_max_stay_by_weekday: toWeekdayFormValues(rate.default_max_stay_by_weekday)
    });



  async function loadCategories() {

    // Caso 1 — tarifa sin hermanas
    if (!rate.group_key) {
      setFormData(prev => ({
        ...prev,
        room_category_ids: [rate.room_category_id]
      }));
      return;
    }

    // Caso 2 — tarifa con hermanas (multi-categoría)
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/roomRates/all`,
      { params: { business_unit_id: rate.room_category.business_unit_id } }
    );

    const sisters = data.filter(r => r.group_key === rate.group_key);

    const ids = sisters.map(r => r.room_category_id);

    setFormData(prev => ({
      ...prev,
      room_category_ids: ids
    }));
  }

  loadCategories();
}, [rate]);


  // ===============================
  // LOAD ROOM CATEGORIES & CLIENTS
  // ===============================
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/room_categories`)
      .then((res) => {
        const filtered = res.data.filter(
          (cat) => cat.business_unit_id == businessUnitId
        );
        setRoomCategories(filtered);
      });

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/clients`)
      .then((res) => setClients(res.data));
  }, [businessUnitId]);

  // ===============================
  // RECALC CAPACITY LIMITS
  // ===============================
  useEffect(() => {
    if (!formData) return;

    const selected = formData.room_category_ids;
    if (!selected?.length) {
      setCapacityLimits({ adults: null, children: null, max_occupancy: null });
      return;
    }

    const selectedCats = roomCategories.filter((c) =>
      selected.includes(Number(c.id))
    );

    const numsAdults = selectedCats.map((c) => Number(c.max_adults));
    const numsChildren = selectedCats.map((c) => Number(c.max_children));

    const numsOcc = selectedCats
      .map(c => (c.max_occupancy === null || typeof c.max_occupancy === "undefined" ? null : Number(c.max_occupancy)))
      .filter(v => v !== null && !isNaN(v));

    const minAdults =
      numsAdults.length && isFinite(Math.min(...numsAdults))
        ? Math.min(...numsAdults)
        : null;
    const minChildren =
      numsChildren.length && isFinite(Math.min(...numsChildren))
        ? Math.min(...numsChildren)
        : null;

    const minOcc =
      numsOcc.length && isFinite(Math.min(...numsOcc))
        ? Math.min(...numsOcc)
        : null;

    setCapacityLimits({ adults: minAdults, children: minChildren, max_occupancy: minOcc });

  }, [formData?.room_category_ids, roomCategories]);

  // ===============================
  // HANDLE CHANGE
  // ===============================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "room_category_id") {
      const id = Number(value);
      if (!isNaN(id) && id > 0) {
        setFormData((prev) => ({
          ...prev,
          room_category_ids: prev.room_category_ids.includes(id)
            ? prev.room_category_ids
            : [...prev.room_category_ids, id],
          room_category_id: ""
        }));
      }
      return;
    }

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value
      };

      if (name === "adults" && value === "") next.adults_match_type = "exact";
      if (name === "children" && value === "") next.children_match_type = "upto";
      if (name === "max_occupancy" && value === "") next.max_occupancy_match_type = "upto";


      return next;
    });

  };

  const handleWeekdayStayChange = (field, weekday, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [weekday]: value,
      },
    }));
  };

  // ===============================
  // SUBMIT
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;

    const parsedDefaultMinStay =
      formData.default_min_stay === "" ? null : Number(formData.default_min_stay);
    const parsedDefaultMaxStay =
      formData.default_max_stay === "" ? null : Number(formData.default_max_stay);

    if (
      parsedDefaultMinStay !== null &&
      (!Number.isInteger(parsedDefaultMinStay) || parsedDefaultMinStay < 1)
    ) {
      alert("Default Min Stay must be an integer >= 1 (or empty).");
      return;
    }
    if (
      parsedDefaultMaxStay !== null &&
      (!Number.isInteger(parsedDefaultMaxStay) || parsedDefaultMaxStay < 1)
    ) {
      alert("Default Max Stay must be an integer >= 1 (or empty).");
      return;
    }

    const minByWeekdayResult = normalizeWeekdayPayload(formData.default_min_stay_by_weekday);
    const maxByWeekdayResult = normalizeWeekdayPayload(formData.default_max_stay_by_weekday);

    if (minByWeekdayResult.error) {
      alert(minByWeekdayResult.error);
      return;
    }
    if (maxByWeekdayResult.error) {
      alert(maxByWeekdayResult.error);
      return;
    }

    const useWeekdayDefaults = !!formData.use_stay_defaults_by_weekday;
    const minByWeekday = useWeekdayDefaults ? minByWeekdayResult.value : null;
    const maxByWeekday = useWeekdayDefaults ? maxByWeekdayResult.value : null;

    const stayValidationError = validateStayRanges({
      minDefault: parsedDefaultMinStay,
      maxDefault: parsedDefaultMaxStay,
      minByWeekday,
      maxByWeekday,
    });
    if (stayValidationError) {
      alert(stayValidationError);
      return;
    }

    const precisionError = getRoomRateEditPrecisionError(formData, currencyCatalog);
    if (precisionError) {
      alert(precisionError);
      return;
    }

    try {
      const restFormData = { ...formData };
      delete restFormData.use_stay_defaults_by_weekday;

      const payload = {
        ...restFormData,
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null,

        // NO mandar "" al UNIQUE; usar null
        group_key:
          String(formData.group_key ?? "").trim() === ""
            ? null
            : String(formData.group_key).trim(),

        room_category_id: formData.room_category_ids.length === 1
            ? formData.room_category_ids[0]
            : null,

        client_id: formData.client_id || null,

        // NUNCA enviar "" al enum
        penalty_type:
            formData.penalty_type === "" ? null : formData.penalty_type,

        penalty_amount:
            formData.penalty_amount === "" ? null : Number(formData.penalty_amount),

        adults:
            formData.adults === "" ? null : Number(formData.adults),

        adults_match_type:
            (formData.adults_match_type || "exact"),

        children:
            formData.children === "" ? null : Number(formData.children),

        children_match_type:
            (formData.children_match_type || "upto"),

        max_occupancy:
            formData.max_occupancy === "" ? null : Number(formData.max_occupancy),

        max_occupancy_match_type:
            formData.max_occupancy === "" ? null : (formData.max_occupancy_match_type || "upto"),

        room_category_ids: formData.room_category_ids.map(Number),


        is_refundable:
            formData.is_refundable === true ||
            formData.is_refundable === "true",

        // NEW: thresholds + default stays (empty -> null)
        min_price_amount: formData.min_price_amount === "" ? null : Number(formData.min_price_amount),
        max_price_amount: formData.max_price_amount === "" ? null : Number(formData.max_price_amount),
        default_min_stay: parsedDefaultMinStay,
        default_max_stay: parsedDefaultMaxStay,
        default_min_stay_by_weekday: minByWeekday,
        default_max_stay_by_weekday: maxByWeekday,
      };



      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/roomRates/${rate.id}`,
        payload
      );

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error updating Room Rate:", err);
      alert("Error updating Room Rate.");
    }
  };


  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow || "";
    };
  }, []);

  if (!formData) return null;

  const hasCategories = formData.room_category_ids.length > 0;
  const occupancySummary = hasCategories
    ? [
        capacityLimits.adults && capacityLimits.adults > 0
          ? `Adults up to ${capacityLimits.adults}`
          : "Adults unrestricted",
        typeof capacityLimits.children === "number" && capacityLimits.children >= 0
          ? `Children up to ${capacityLimits.children}`
          : "Children unrestricted",
        capacityLimits.max_occupancy !== null && capacityLimits.max_occupancy > 0
          ? `Max occupancy ${capacityLimits.max_occupancy}`
          : null,
      ]
        .filter(Boolean)
        .join(" ? ")
    : "Select categories to derive occupancy caps.";

  const weekdayToggleLabel = formData.use_stay_defaults_by_weekday
    ? "Use defaults across all weekdays"
    : "Define min/max stay per weekday";

  const overlay = (
    <div
      className="erp-roomRateModal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(7, 15, 36, 0.78)",
        zIndex: 22000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "14px",
        "--erp-modal-bg": isLightTheme ? "#ffffff" : "#0e1730",
        "--erp-modal-section": isLightTheme ? "#f7fafc" : "#121f3d",
        "--erp-modal-section-strong": isLightTheme ? "#edf8fc" : "#152344",
        "--erp-modal-input": isLightTheme ? "#ffffff" : "#152344",
        "--erp-modal-title": isLightTheme ? "#172653" : "#f2f6ff",
        "--erp-modal-text": isLightTheme ? "#26354f" : "#dfe8f7",
        "--erp-modal-muted": isLightTheme ? "#5b6680" : "#aebbd0",
        "--erp-modal-border": isLightTheme ? "#d7e2ec" : "rgba(92,134,181,0.34)",
        "--erp-modal-border-strong": isLightTheme ? "#7c90a7" : "rgba(61,182,233,0.58)",
        "--erp-modal-accent-soft": isLightTheme ? "#edf8fc" : "rgba(61,182,233,0.12)",
        "--erp-modal-active-text": isLightTheme ? "#223a7a" : "#dff6ff",
        "--erp-modal-positive": isLightTheme ? "#216b5a" : "#a9e1da",
        "--erp-modal-positive-soft": isLightTheme ? "#edf9f8" : "rgba(99,193,199,0.12)",
        "--erp-modal-danger": isLightTheme ? "#9b3445" : "#ffc7cf",
        "--erp-modal-danger-border": isLightTheme ? "#d48a97" : "rgba(255,120,138,0.55)",
        "--erp-modal-danger-soft": isLightTheme ? "#fdeff1" : "rgba(155,52,69,0.16)",
        "--erp-modal-shadow": isLightTheme ? "0 18px 44px rgba(34,58,122,0.16)" : "0 24px 54px rgba(2,8,28,0.52)"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "980px",
          maxHeight: "calc(100dvh - 28px)",
          overflowY: "auto"
        }}
      >
        <div style={formShellStyle}>
          <div style={{ textAlign: "center", marginBottom: "22px" }}>
            <p
              style={{
                letterSpacing: "0.35em",
                fontSize: "0.8rem",
                color: "var(--erp-modal-muted)",
                marginBottom: "6px",
                textTransform: "uppercase"
              }}
            >
              Room rate studio
            </p>
            <h2 style={{ margin: 0, fontSize: "1.55rem", color: "var(--erp-modal-title)" }}>
              Edit room rate
            </h2>
            <p style={{ marginTop: "6px", color: "var(--erp-modal-muted)" }}>
              {rate?.name} ({rate?.rate_code})
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Rate essentials</h3>
                  <p style={sectionTextStyle}>
                    Code, category set and currency that identify the rate.
                  </p>
                </div>
                <span style={formData.status === "active" ? statusBadgeStyle : muteBadgeStyle}>
                  {formData.status === "active" ? "Active" : "Inactive"}
                </span>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Rate Code *</label>
                  <input
                    type="text"
                    name="rate_code"
                    value={formData.rate_code}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    required
                    maxLength={6}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Room category</label>
                  <select
                    name="room_category_id"
                    value={formData.room_category_id}
                    onChange={handleChange}
                    style={selectFieldStyle}
                  >
                    <option value="">Select a category to add below</option>
                    {roomCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p style={hintTextStyle}>Add categories one by one; they appear as chips below.</p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Selected categories</label>
                  <div style={chipWrapperStyle}>
                    {formData.room_category_ids.length === 0 && (
                      <span style={{ color: "var(--erp-modal-muted)", fontSize: "0.85rem" }}>
                        No categories selected yet.
                      </span>
                    )}
                    {formData.room_category_ids.map((id) => {
                      const cat = roomCategories.find((c) => Number(c.id) === Number(id));
                      const label = cat ? cat.name : `#${id}`;
                      return (
                        <span key={id} style={chipTagStyle}>
                          {label}
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => {
                                const next = prev.room_category_ids.filter((x) => Number(x) !== Number(id));
                                return { ...prev, room_category_ids: next };
                              })
                            }
                            style={chipCloseStyle}
                            aria-label={`Remove ${label}`}
                            title="Remove"
                          >
                            {"×"}
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Group key (optional)</label>
                  <input
                    type="text"
                    name="group_key"
                    value={formData.group_key}
                    onChange={handleChange}
                    style={inputFieldStyle}
                  />
                  <p style={hintTextStyle}>Use to append new categories to an existing group.</p>
                </div>
                <div>
                  <label style={labelStyle}>Client</label>
                  <select name="client_id" value={formData.client_id} onChange={handleChange} style={selectFieldStyle}>
                    <option value="">None</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <input
                    type="text"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    style={inputFieldStyle}
                  />
                </div>
              </div>
            </section>

            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Visibility + channel</h3>
                  <p style={sectionTextStyle}>Control what status and channels this rate is listed on.</p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select style={selectFieldStyle} name="status" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <p style={hintTextStyle}>Inactive rates stay off the board until reactivated.</p>
                </div>
                <div>
                  <label style={labelStyle}>Booking engine exposure</label>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, show_in_booking_engine: !prev.show_in_booking_engine }))}
                    style={{
                      ...toggleButtonBase,
                      borderColor: formData.show_in_booking_engine ? "#3db6e9" : "var(--erp-modal-border-strong)",
                      background: formData.show_in_booking_engine ? "var(--erp-modal-accent-soft)" : "var(--erp-modal-input)",
                      color: formData.show_in_booking_engine ? "var(--erp-modal-active-text)" : "var(--erp-modal-text)",
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    {formData.show_in_booking_engine ? "Visible in booking engine" : "Hidden from booking engine"}
                  </button>
                  <p style={hintTextStyle}>Toggle to keep the rate private or publish to guests.</p>
                </div>
              </div>
            </section>

            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Occupancy & limits</h3>
                  <p style={sectionTextStyle}>{occupancySummary}</p>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "16px" }}>
                <div>
                  <label style={labelStyle}>Adults rule</label>
                  <select
                    name="adults_match_type"
                    value={formData.adults_match_type}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={formData.adults === ""}
                  >
                    <option value="exact">Exact</option>
                    <option value="upto">Up to</option>
                  </select>
                  <p style={hintTextStyle}>Only active when a value is selected.</p>
                </div>
                <div>
                  <label style={labelStyle}>Adults</label>
                  <select
                    name="adults"
                    value={formData.adults}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={!capacityLimits.adults || capacityLimits.adults <= 0}
                  >
                    <option value="">Any</option>
                    {Array.from({ length: capacityLimits.adults || 0 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Children rule</label>
                  <select
                    name="children_match_type"
                    value={formData.children_match_type}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={formData.children === ""}
                  >
                    <option value="upto">Up to</option>
                    <option value="exact">Exact</option>
                  </select>
                  <p style={hintTextStyle}>Applies only once children are explicitly set.</p>
                </div>
                <div>
                  <label style={labelStyle}>Children</label>
                  <select
                    name="children"
                    value={formData.children}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={capacityLimits.children === null}
                  >
                    <option value="">Any</option>
                    <option value={0}>0</option>
                    {Array.from({ length: capacityLimits.children || 0 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Max occupancy rule</label>
                  <select
                    name="max_occupancy_match_type"
                    value={formData.max_occupancy_match_type}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={formData.max_occupancy === ""}
                  >
                    <option value="upto">Up to</option>
                    <option value="exact">Exact</option>
                  </select>
                  <p style={hintTextStyle}>Optional rules for the combined occupancy cap.</p>
                </div>
                <div>
                  <label style={labelStyle}>Max occupancy</label>
                  <select
                    name="max_occupancy"
                    value={formData.max_occupancy}
                    onChange={handleChange}
                    style={selectFieldStyle}
                    disabled={capacityLimits.max_occupancy === null}
                  >
                    <option value="">Any</option>
                    {Array.from({ length: capacityLimits.max_occupancy || 0 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <p style={hintTextStyle}>Applies only if the selected categories define a max occupancy.</p>
                </div>
              </div>
            </section>

            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Price & duration</h3>
                  <p style={sectionTextStyle}>Reflect floor/ceiling values and standard lengths.</p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Minimum price</label>
                  <input
                    type="number"
                    step={currencyAmountStep(formData.currency, currencyCatalog)}
                    min="0"
                    name="min_price_amount"
                    value={formData.min_price_amount}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    placeholder="No limit"
                  />
                  <p style={hintTextStyle}>Rate prices will not dip below this.</p>
                </div>
                <div>
                  <label style={labelStyle}>Maximum price</label>
                  <input
                    type="number"
                    step={currencyAmountStep(formData.currency, currencyCatalog)}
                    min="0"
                    name="max_price_amount"
                    value={formData.max_price_amount}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    placeholder="No limit"
                  />
                  <p style={hintTextStyle}>Rate prices will not exceed this.</p>
                </div>
                <div>
                  <label style={labelStyle}>Default min stay</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    name="default_min_stay"
                    value={formData.default_min_stay}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    placeholder="No default"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Default max stay</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    name="default_max_stay"
                    value={formData.default_max_stay}
                    onChange={handleChange}
                    style={inputFieldStyle}
                    placeholder="No default"
                  />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => {
                        if (prev.use_stay_defaults_by_weekday) {
                          return { ...prev, use_stay_defaults_by_weekday: false };
                        }

                        const minByWeekday = { ...prev.default_min_stay_by_weekday };
                        const maxByWeekday = { ...prev.default_max_stay_by_weekday };

                        for (const day of WEEKDAY_OPTIONS) {
                          if (minByWeekday[day.key] === "" && prev.default_min_stay !== "") {
                            minByWeekday[day.key] = String(prev.default_min_stay);
                          }
                          if (maxByWeekday[day.key] === "" && prev.default_max_stay !== "") {
                            maxByWeekday[day.key] = String(prev.default_max_stay);
                          }
                        }

                        return {
                          ...prev,
                          use_stay_defaults_by_weekday: true,
                          default_min_stay_by_weekday: minByWeekday,
                          default_max_stay_by_weekday: maxByWeekday,
                        };
                      });
                    }}
                    style={{
                      ...toggleButtonBase,
                      borderColor: formData.use_stay_defaults_by_weekday
                        ? "#3db6e9"
                        : "var(--erp-modal-border-strong)",
                      background: formData.use_stay_defaults_by_weekday ? "var(--erp-modal-accent-soft)" : "var(--erp-modal-input)",
                      color: formData.use_stay_defaults_by_weekday ? "var(--erp-modal-active-text)" : "var(--erp-modal-text)",
                      width: "max(220px, 40%)"
                    }}
                  >
                    {weekdayToggleLabel}
                  </button>
                  <p style={hintTextStyle}>Enable per-weekday min/max stays for granular control.</p>
                </div>
              </div>

              {formData.use_stay_defaults_by_weekday && (
                <div style={weekdayGridStyle}>
                  {WEEKDAY_OPTIONS.map((day) => (
                    <div key={day.key} style={weekdayCardStyle}>
                      <span style={{ fontWeight: 700, color: "var(--erp-modal-title)" }}>{day.label}</span>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="Min"
                        value={formData.default_min_stay_by_weekday[day.key]}
                        onChange={(e) =>
                          handleWeekdayStayChange("default_min_stay_by_weekday", day.key, e.target.value)
                        }
                        style={weekdayInputStyle}
                      />
                      <input
                        type="number"
                        step="1"
                        min="1"
                        placeholder="Max"
                        value={formData.default_max_stay_by_weekday[day.key]}
                        onChange={(e) =>
                          handleWeekdayStayChange("default_max_stay_by_weekday", day.key, e.target.value)
                        }
                        style={weekdayInputStyle}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <div>
                  <h3 style={sectionTitleStyle}>Policies & timeline</h3>
                  <p style={sectionTextStyle}>Define validity windows, cancellation rules and penalties.</p>
                </div>
              </div>
              <div style={fieldGridStyle}>
                <div>
                  <label style={labelStyle}>Valid from *</label>
                  <input type="date" name="valid_from" value={formData.valid_from} onChange={handleChange} style={inputFieldStyle} required />
                </div>
                <div>
                  <label style={labelStyle}>Valid to</label>
                  <input type="date" name="valid_to" value={formData.valid_to} onChange={handleChange} style={inputFieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Refundable</label>
                  <select name="is_refundable" value={formData.is_refundable} onChange={handleChange} style={selectFieldStyle}>
                    <option value={true}>Yes</option>
                    <option value={false}>No</option>
                  </select>
                  <p style={hintTextStyle}>Choose yes to allow refunds for this rate.</p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Cancellation policy</label>
                  <textarea name="cancellation_policy" value={formData.cancellation_policy} onChange={handleChange} style={textareaFieldStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Penalty type</label>
                  <select name="penalty_type" value={formData.penalty_type} onChange={handleChange} style={selectFieldStyle}>
                    <option value="">None</option>
                    <option value="fixed">Fixed</option>
                    <option value="percent">Percent</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Penalty amount</label>
                  <input
                    type="number"
                    step={formData.penalty_type === "fixed" ? currencyAmountStep(formData.currency, currencyCatalog) : "0.01"}
                    name="penalty_amount"
                    value={formData.penalty_amount}
                    onChange={handleChange}
                    style={inputFieldStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Penalty description</label>
                  <input type="text" name="penalty_description" value={formData.penalty_description} onChange={handleChange} style={inputFieldStyle} />
                </div>
              </div>
            </section>

            <div style={fieldActionsRowStyle}>
              <button type="submit" style={primaryButtonStyle}>
                Save changes
              </button>
              {onCancel && (
                <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== "undefined") {
    return ReactDOM.createPortal(overlay, document.body);
  }

  return overlay;
}
